import { Component, OnDestroy, OnInit } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'

import { TranslateService } from '@ngx-translate/core'
import { filter, Subscription } from 'rxjs'

import { SharedModule } from '@@shared/shared.module'
import { MobileAppBridgeService } from '@@services/mobile-app-bridge.service'
import { ToastService } from '@@services/toast.service'

export const PORTAL_HOME_URL = '/portal/menu'

/** Body class that pushes the fixed header and side-nav below the bar. */
export const APP_BAR_VISIBLE_BODY_CLASS = 'app-bar-visible'

const pathOf = (url: string): string => url.split('?')[0].split('#')[0]

/** True when the URL's path (ignoring query/fragment) is the modules home. */
export const isHomeUrl = (url: string): boolean => {
    const path = pathOf(url)

    return path === PORTAL_HOME_URL || path === PORTAL_HOME_URL + '/'
}

/** Sign-in pages (app entry, signed-out screen) carry their own actions. */
export const isAccessUrl = (url: string): boolean =>
    pathOf(url).startsWith('/portal/access')

/**
 * DV-5499 (PRD FR-12..14): fixed bar at the top of the portal while it is
 * open inside the mobile app's web view. "Back to App" hands control back to
 * the app; "All modules" returns to the menu from inside a module.
 *
 * "Back to App" only shows when the web view offers a way back (a bridge or
 * an allowed return URL). Without one — the current Android build, which
 * draws its own native back button — the button would do nothing, so it is
 * hidden; the bar then only appears where "All modules" is useful.
 */
@Component({
    standalone: true,
    imports: [SharedModule],
    selector: 'in-back-to-app-bar',
    templateUrl: './back-to-app-bar.component.html',
    styleUrls: ['./back-to-app-bar.component.scss']
})
export class BackToAppBarComponent implements OnInit, OnDestroy {
    appMode = false
    canGoBack = false
    showHomeLink = false
    visible = false

    private onAccessPage = false
    private subscriptions = new Subscription()

    constructor(
        private router: Router,
        private mobileAppBridge: MobileAppBridgeService,
        private toastService: ToastService,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        this.updateForUrl(this.router.url)

        this.subscriptions.add(
            this.mobileAppBridge.appMode$.subscribe((appMode) => {
                this.appMode = appMode
                this.canGoBack = this.mobileAppBridge.canReturnToApp()
                this.updateVisibility()
            })
        )

        this.subscriptions.add(
            this.router.events
                .pipe(
                    filter(
                        (event): event is NavigationEnd =>
                            event instanceof NavigationEnd
                    )
                )
                .subscribe((event) =>
                    this.updateForUrl(event.urlAfterRedirects)
                )
        )
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe()
        document.body.classList.remove(APP_BAR_VISIBLE_BODY_CLASS)
    }

    backToApp(): void {
        if (this.mobileAppBridge.backToApp()) {
            return
        }

        // Nothing reached the app: stop offering the button and say how to leave.
        this.canGoBack = false
        this.updateVisibility()
        this.toastService.info(
            this.translate.instant('layout.back-to-app-bar.use-app-back')
        )
    }

    updateForUrl(url: string): void {
        this.onAccessPage = isAccessUrl(url)
        this.showHomeLink = !isHomeUrl(url) && !this.onAccessPage
        this.canGoBack = this.mobileAppBridge.canReturnToApp()
        this.updateVisibility()
    }

    private updateVisibility(): void {
        this.visible =
            this.appMode &&
            !this.onAccessPage &&
            (this.canGoBack || this.showHomeLink)

        document.body.classList.toggle(APP_BAR_VISIBLE_BODY_CLASS, this.visible)
    }
}
